import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Lịch làm việc gốc của một người — APM-1310a.
 *
 * ## Một ngày gồm nhiều CA, không phải một khoảng trừ đi phút nghỉ
 *
 * Bản đầu là `{ start, end, breakMinutes }`. Nó sai theo ba hướng cùng lúc:
 * người dùng phải tự nhẩm rằng 09:00–18:00 nghỉ 60 phút thì làm tám tiếng;
 * nghỉ trưa **lúc nào** không được ghi lại nên không xếp được họp vào đúng chỗ
 * trống; và một người làm hai ca rời nhau (sáng ở dự án này, chiều ở chỗ khác)
 * không diễn đạt nổi.
 *
 * Danh sách ca thì nghỉ trưa **là** khoảng trống giữa hai ca — không phải một
 * con số phải nhớ trừ. Và nó mở ra thứ mà mô hình cũ chặn: nghỉ nửa ngày
 * (APM-1311) chính là nghỉ **một ca**, không cần thêm khái niệm nào.
 *
 * ## Vì sao phép tính năng lực nằm ở đây chứ không ở API
 *
 * Cùng một câu hỏi được hỏi ở hai bên: server tính trần năng lực để dựng biểu
 * đồ tải, còn form khai lịch phải hiện "tuần này bạn làm bao nhiêu giờ" ngay
 * lúc gõ, trước khi bấm lưu. Hai bản cài đặt của cùng một phép cộng sẽ lệch
 * nhau, và lệch vài giờ thì không ai phát hiện ra.
 *
 * ## Chưa khai lịch là trạng thái HỢP LỆ
 *
 * `DEFAULT_WORK_SCHEDULE` giữ nguyên hành vi có từ trước APM-1310a: T2–T6, tám
 * tiếng. Nó là **mặc định trong mã nguồn**, không phải dữ liệu được seed —
 * không hàng nào được sinh sẵn cho người đang có, và không hook nào sinh lúc
 * tạo tài khoản. Cả hai đều biến "chưa khai" thành dữ liệu giả mà không ai biết
 * là giả, và `WorkScheduleView.isDefault` sẽ mất khả năng nói ra sự thật đó.
 */
/** `0` = Chủ nhật … `6` = thứ Bảy — đúng bằng `Date.getUTCDay()`. */
export const Weekday = z.number().int().min(0).max(6);
/**
 * Giờ trong ngày dạng `HH:mm`, 24 giờ.
 *
 * API phơi ra chuỗi còn DB lưu phút-từ-nửa-đêm: chuỗi là thứ người đọc và thứ
 * `<input type="time">` nhận, còn số là thứ so sánh và trừ được mà không phải
 * phân tích cú pháp lại ở mỗi phép tính.
 */
export const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Giờ phải có dạng HH:mm');
export function minutesFromTimeOfDay(value) {
    const [hours, minutes] = value.split(':');
    return Number(hours) * 60 + Number(minutes);
}
export function timeOfDayFromMinutes(minutes) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mm = String(minutes % 60).padStart(2, '0');
    return `${hh}:${mm}`;
}
/** Một ca làm việc liền mạch. Nghỉ giữa buổi là khoảng trống **giữa** hai ca. */
export const WorkShift = z.object({ start: TimeOfDay, end: TimeOfDay });
/** Một ngày trong tuần mà người đó có làm việc. Ngày không làm thì **không có** mục. */
export const WorkScheduleDayEntry = z.object({
    weekday: Weekday,
    /** Ít nhất một ca — một ngày làm việc không có ca nào thì nó là ngày nghỉ. */
    shifts: z.array(WorkShift).min(1).max(4),
});
export function shiftHours(shift) {
    const minutes = minutesFromTimeOfDay(shift.end) - minutesFromTimeOfDay(shift.start);
    // Không bao giờ trả số âm: `SetWorkScheduleInput` đã chặn lúc nhập, nhưng một
    // hàng cũ trong DB không đi qua cửa đó, và một trần năng lực **âm** làm mọi
    // phép so "quá tải chưa" trả lời ngược.
    return Math.max(0, Math.round((minutes / 60) * 100) / 100);
}
/** Số giờ làm việc thật của một ngày: tổng độ dài các ca. */
export function dayCapacityHours(entry) {
    return Math.round(entry.shifts.reduce((sum, shift) => sum + shiftHours(shift), 0) * 100) / 100;
}
/** Tổng giờ một tuần — con số mà form khai lịch hiện ngay lúc gõ. */
export function weeklyHours(days) {
    return Math.round(days.reduce((sum, day) => sum + dayCapacityHours(day), 0) * 100) / 100;
}
/**
 * Mặc định khi chưa ai khai lịch: T2–T6, sáng 09:00–12:00 và chiều 13:00–18:00.
 *
 * Tám tiếng mỗi ngày làm việc — **đúng bằng** hằng số `DEFAULT_CAPACITY_HOURS`
 * mà `WorkloadService` dùng trước APM-1310a. Giữ nguyên con số ấy có chủ đích:
 * bật tính năng này lên không được làm mọi biểu đồ tải đang có nhảy số.
 */
export const DEFAULT_WORK_SCHEDULE = [1, 2, 3, 4, 5].map((weekday) => ({
    weekday,
    shifts: [
        { start: '09:00', end: '12:00' },
        { start: '13:00', end: '18:00' },
    ],
}));
/**
 * ⚠️ Ít nhất một ngày — lịch rỗng bị **chặn**, không phải được nhận.
 *
 * "Chưa khai" được mã hoá bằng **không có hàng nào** trong `work_schedule_shift`,
 * nên một lịch rỗng lưu được sẽ không phân biệt nổi với chưa khai: người dùng
 * bỏ tích cả bảy ngày, bấm lưu, và màn hình đọc lại ra T2–T6 tám tiếng cùng
 * dòng chữ "chưa khai lịch". Lặng lẽ trả lời ngược hẳn thứ vừa được nói.
 *
 * Và đó cũng là mô hình đúng: "không làm ngày nào" là nghỉ phép hoặc đã nghỉ
 * việc (APM-1311) — hai thứ có ngày bắt đầu, ngày kết thúc và lý do — chứ không
 * phải một tuần làm việc trống rỗng kéo dài vô hạn.
 */
export const SetWorkScheduleInput = z
    .object({ days: z.array(WorkScheduleDayEntry).min(1).max(7) })
    .superRefine((value, ctx) => {
    const seen = new Set();
    for (const day of value.days) {
        if (seen.has(day.weekday)) {
            ctx.addIssue({ code: 'custom', message: 'Mỗi ngày trong tuần chỉ được khai một lần' });
        }
        seen.add(day.weekday);
        const sorted = [...day.shifts].sort((a, b) => minutesFromTimeOfDay(a.start) - minutesFromTimeOfDay(b.start));
        let previousEnd = -1;
        for (const shift of sorted) {
            const start = minutesFromTimeOfDay(shift.start);
            const end = minutesFromTimeOfDay(shift.end);
            if (end <= start) {
                ctx.addIssue({ code: 'custom', message: 'Giờ kết thúc phải sau giờ bắt đầu' });
                continue;
            }
            // Hai ca chồng nhau thì tổng giờ của ngày đếm đôi phần giao — trần năng
            // lực cao hơn thực tế, và cao theo hướng không ai kiểm lại.
            if (start < previousEnd) {
                ctx.addIssue({ code: 'custom', message: 'Hai ca làm việc chồng lên nhau' });
            }
            previousEnd = end;
        }
    }
});
export const WorkScheduleView = z.object({
    userId: z.uuid(),
    days: z.array(WorkScheduleDayEntry),
    weeklyHours: z.number().nonnegative(),
    /**
     * `true` = người này **chưa khai** lịch, `days` là mặc định của tổ chức
     * (`OrgWorkScheduleView`), hoặc của hệ thống nếu tổ chức cũng chưa đặt.
     *
     * Không có cờ này thì màn hình không phân biệt được "đã cân nhắc và chọn
     * T2–T6" với "chưa ai đụng vào", và PM sẽ lập kế hoạch dựa trên một lịch mà
     * chủ nhân của nó chưa từng xác nhận.
     */
    isDefault: z.boolean(),
});
/**
 * Lịch làm việc mặc định của **tổ chức** — APM-OWS-a.
 *
 * Người chưa khai lịch riêng rơi về lịch này thay vì `DEFAULT_WORK_SCHEDULE`.
 * Nó là mặc định được **đọc lúc tính**, không phải khuôn được chép cho người
 * mới: admin sửa lịch tổ chức thì mọi người chưa khai lịch riêng đổi theo, kể
 * cả người đã vào từ trước. Ai đã khai lịch riêng thì giữ nguyên lịch của mình.
 *
 * Sửa bằng chính `SetWorkScheduleInput` — cùng luật (ít nhất một ngày, ca không
 * chồng nhau) vì nó rơi vào cùng những phép tính năng lực.
 */
export const OrgWorkScheduleView = z.object({
    days: z.array(WorkScheduleDayEntry),
    weeklyHours: z.number().nonnegative(),
    /** `true` = tổ chức **chưa đặt**, `days` là mặc định của hệ thống (T2–T6, tám tiếng). */
    isDefault: z.boolean(),
});
/**
 * Vì sao một ngày không làm việc được — APM-1319c.
 *
 * Bốn lý do, vì bốn cách nói khác nhau trên màn hình: "cuối tuần" khác "nghỉ
 * lễ" khác "nghỉ phép", và người xem cần biết cái nào để biết có đổi được
 * không. Gộp thành một cờ `isDayOff` thì nhãn chỉ còn nói được "Nghỉ".
 */
export const DayOffReason = z.enum(['off_shift', 'holiday', 'absence', 'left']);
export const DayOff = z.object({
    date: CalendarDate,
    reason: DayOffReason,
});
/**
 * Những ngày **không làm việc** của chính người gọi, trong một khoảng.
 *
 * Chỉ chứa ngày trần năng lực bằng **0**. Ngày nghỉ nửa buổi vẫn là ngày làm
 * việc: che nó đi thì lịch giấu mất phần việc thật sự có.
 *
 * `absence` chỉ đến từ đơn nghỉ **đã duyệt** (APM-MYT-e): đơn chờ duyệt chưa
 * phải ngày nghỉ trên lịch, dù trần năng lực đã trừ nó.
 */
export const DaysOffView = z.object({
    from: CalendarDate,
    to: CalendarDate,
    days: z.array(DayOff),
});
//# sourceMappingURL=work-schedule.js.map